/**
 * The full results page.
 *
 * Ranked by relevance, twenty-five at a time, reachable to five hundred.
 *
 * The ceiling is about DISPLAY, never about what was searched. Every policy
 * the reader may see is searched and ranked; past five hundred the tail is
 * simply not reachable by paging, and the page says so rather than letting
 * somebody believe they have seen everything. The remedy offered is the facet
 * row, not a longer list.
 *
 * Status defaults to published because that is what a policy library is for —
 * the version in force. Roles that may see unpublished work can widen it, and
 * the control is absent for roles that may not, rather than present and
 * refusing.
 */

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SearchBox } from './SearchBox';
import { Highlight } from './HighlightText';
import { useFullSearch, isSearchable, PAGE_SIZE, MIN_QUERY_LENGTH } from './useSearch';
import { useFacetLabels } from './useFacetLabels';
import { pageWindow } from './pagination';
import { bestSnippet } from './highlight';
import { activeFacetCount } from './searchQuery';
import { FACET_FIELDS, FACET_LABELS, type FacetField, type FacetSelection } from './types';
import styles from './SearchResults.module.css';

const SNIPPET_SOURCE_LABEL: Record<string, string> = {
  content: 'in the policy text',
  attachment: 'in an attachment',
};

function formatDate(ms: number | undefined): string | null {
  if (!ms) return null;
  return new Date(ms).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function SearchResultsPage() {
  const [params, setParams] = useSearchParams();

  const query = params.get('q') ?? '';
  const page = Math.max(0, Number(params.get('page') ?? '0') || 0);

  // The URL is the state. A results page you cannot send to a colleague is
  // half a feature, and the back button should retrace filters too.
  //
  // Two selections, and the difference matters. `chosen` is what the reader
  // actually asked for; `facets` adds the published default on top. Only
  // `chosen` decides whether to search at all — otherwise arriving at /search
  // with an empty box would count the default as a filter and immediately
  // fetch every published policy in the hospital, which is the behaviour this
  // page exists to avoid.
  const chosen = useMemo<FacetSelection>(() => {
    const selection: FacetSelection = {};
    for (const field of FACET_FIELDS) {
      const value = params.get(field);
      // "all" is how the status control says "no restriction"; it is not a
      // value to filter on.
      if (value && !(field === 'status' && value === 'all')) selection[field] = value;
    }
    return selection;
  }, [params]);

  const facets = useMemo<FacetSelection>(() => {
    // A policy library is about the version in force, so published is the
    // default view. Anyone whose role lets them see more can widen it.
    if (params.get('status') === 'all' || chosen.status) return chosen;
    return { ...chosen, status: 'published' };
  }, [chosen, params]);

  const asked = query.trim().length > 0 || Object.keys(chosen).length > 0;

  const { data, isFetching, error, key, keyError } = useFullSearch(
    query,
    facets,
    page,
    asked,
  );

  const searchable = asked && isSearchable(query, chosen);

  const hits = data?.hits ?? [];
  const labelFor = useFacetLabels(hits, true);

  function update(changes: Record<string, string | null>) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    }
    // Any change to the question restarts the answer at page one.
    if (!('page' in changes)) next.delete('page');
    setParams(next, { replace: false });
  }

  const from = data ? Math.min(page * PAGE_SIZE + 1, data.reachableHits) : 0;
  const to = data ? Math.min((page + 1) * PAGE_SIZE, data.reachableHits) : 0;
  const filtersOn = activeFacetCount(chosen);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>Search policies</h1>
        <SearchBox initialQuery={query} autoFocus={!query} placeholder="Search policies" />
      </header>

      {keyError && (
        <p className={styles.notice} role="alert">
          Search is unavailable right now. You can still browse from the{' '}
          <Link to="/policies">policies list</Link>.
        </p>
      )}

      {!searchable && !keyError && (
        <p className={styles.hint}>
          Type at least {MIN_QUERY_LENGTH} characters, or pick a filter, to search across
          every policy you have access to — including the text inside the documents.
        </p>
      )}

      {searchable && key && (
        <>
          <div className={styles.facets} role="group" aria-label="Filter results">
            {FACET_FIELDS.map((field) => (
              <FacetSelect
                key={field}
                field={field}
                value={facets[field] ?? ''}
                counts={data?.facets?.[field]}
                labelFor={labelFor}
                canSearchUnpublished={key.canSearchUnpublished}
                onChange={(v) =>
                  update({
                    [field]: field === 'status' && v === '' ? 'all' : v || null,
                  })
                }
              />
            ))}
            {filtersOn > 0 && (
              <button
                type="button"
                className={styles.clearFilters}
                onClick={() => update(Object.fromEntries(FACET_FIELDS.map((f) => [f, null])))}
              >
                Clear {filtersOn} filter{filtersOn === 1 ? '' : 's'}
              </button>
            )}
          </div>

          <div className={styles.summary} role="status" aria-live="polite">
            {error ? (
              <span className={styles.error}>That search could not be completed.</span>
            ) : !data ? (
              <span>Searching…</span>
            ) : data.totalHits === 0 ? (
              <span>
                No policies matched{query ? ` “${query}”` : ''}
                {filtersOn > 0 ? ' with these filters' : ''}.
              </span>
            ) : (
              <span>
                <strong>{data.totalHits.toLocaleString()}</strong>{' '}
                {data.totalHits === 1 ? 'result' : 'results'}
                {query ? ` for “${data.query}”` : ''} · showing {from}–{to}
                {isFetching ? ' · updating…' : ''}
              </span>
            )}
          </div>

          {data?.tooMany && (
            <p className={styles.narrow}>
              Every matching policy was searched and ranked, but only the top{' '}
              {key.maxResults.toLocaleString()} can be opened from here. Narrow with a
              filter above, or add a word, to be sure the one you want is in reach.
            </p>
          )}

          {data && data.hits.length > 0 && (
            <ol className={styles.results} start={from}>
              {data.hits.map((hit) => {
                const snippet = bestSnippet(hit);
                const revised = formatDate(hit.updatedAt ?? hit.publishedAt);
                const meta = [
                  hit.departmentName,
                  hit.documentTypeName,
                  revised ? `Revised ${revised}` : null,
                ].filter(Boolean);

                return (
                  <li key={hit.objectID} className={styles.result}>
                    <Link to={`/policy/${hit.policyId}`} className={styles.resultTitle}>
                      <Highlight
                        value={hit._highlightResult?.title?.value}
                        fallback={hit.title}
                      />
                    </Link>

                    <p className={styles.meta}>
                      {hit.policyNumber && (
                        <span className={styles.number}>
                          <Highlight
                            value={hit._highlightResult?.policyNumber?.value}
                            fallback={hit.policyNumber}
                          />
                        </span>
                      )}
                      {meta.join(' • ')}
                      {hit.status !== 'published' && (
                        <span className={styles.statusPill}>
                          {labelFor('status', hit.status) ?? hit.status}
                        </span>
                      )}
                    </p>

                    {snippet.value && (
                      <p className={styles.snippet}>
                        <Highlight value={snippet.value} />
                        {SNIPPET_SOURCE_LABEL[snippet.source] && (
                          <span className={styles.snippetSource}>
                            {' '}
                            — {SNIPPET_SOURCE_LABEL[snippet.source]}
                          </span>
                        )}
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>
          )}

          {data && data.lastPage > 0 && (
            <Pager
              page={page}
              lastPage={data.lastPage}
              onGo={(p) => update({ page: p === 0 ? null : String(p) })}
            />
          )}
        </>
      )}
    </div>
  );
}

function FacetSelect({
  field,
  value,
  counts,
  labelFor,
  canSearchUnpublished,
  onChange,
}: {
  field: FacetField;
  value: string;
  counts: Record<string, number> | undefined;
  labelFor: (field: FacetField, value: string) => string | null;
  canSearchUnpublished: boolean;
  onChange: (value: string) => void;
}) {
  // A viewer's key already restricts them to published, so a Status control
  // would offer one option and change nothing. Absent beats inert.
  if (field === 'status' && !canSearchUnpublished) return null;

  // An id with no name is dropped, not shown raw. See useFacetLabels.
  const options = Object.entries(counts ?? {})
    .map(([v, count]) => ({ value: v, label: labelFor(field, v), count }))
    .filter((o): o is { value: string; label: string; count: number } => o.label !== null)
    .sort((a, b) => a.label.localeCompare(b.label));

  // The chosen value must stay selectable even when this result set no longer
  // reports it, or changing another filter would silently drop it.
  if (value && !options.some((o) => o.value === value)) {
    options.unshift({ value, label: labelFor(field, value) ?? value, count: 0 });
  }

  // Nothing to choose between means nothing to show. A permanently empty
  // dropdown — Standard, on data that carries no regulatory standards — reads
  // as a broken control, and the project's rule elsewhere is that absent beats
  // inert.
  if (options.length === 0) return null;

  return (
    <label className={styles.facet}>
      <span className={styles.facetLabel}>{FACET_LABELS[field]}</span>
      <select
        className={styles.select}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">All</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
            {o.count > 0 ? ` (${o.count})` : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

function Pager({
  page,
  lastPage,
  onGo,
}: {
  page: number;
  lastPage: number;
  onGo: (page: number) => void;
}) {
  return (
    <nav className={styles.pager} aria-label="Search results pages">
      <button
        type="button"
        className={styles.pageButton}
        onClick={() => onGo(page - 1)}
        disabled={page === 0}
      >
        ‹ Previous
      </button>

      {pageWindow(page, lastPage).map((p, i) =>
        p === null ? (
          <span key={`gap-${i}`} className={styles.gap} aria-hidden="true">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            className={p === page ? `${styles.pageButton} ${styles.current}` : styles.pageButton}
            aria-current={p === page ? 'page' : undefined}
            aria-label={`Page ${p + 1}`}
            onClick={() => onGo(p)}
          >
            {p + 1}
          </button>
        ),
      )}

      <button
        type="button"
        className={styles.pageButton}
        onClick={() => onGo(page + 1)}
        disabled={page >= lastPage}
      >
        Next ›
      </button>
    </nav>
  );
}
